import type { ContractBlock } from "@closer/shared";

/**
 * BİR BLOĞUN SALT OKUNUR ÇİZİMİ — biçimiyle birlikte (ADR-0062).
 *
 * ### ⚠ Neden ortak bileşen
 *
 * Aynı blok İKİ yüzeyde çiziliyor: ön izleme (`ContractDocumentPage`) ve ileride başka
 * salt okunur yüzeyler. İki kopya yazsaydık, içe aktarılan bir DOCX'in başlığı bir ekranda
 * başlık, ötekinde düz metin görünürdü — kullanıcı hangisinin doğru olduğunu bilemezdi.
 * Bu depoda `UyeListesiDurumu` ve `WorkflowCancelDialog` aynı gerekçeyle ortaklaştırıldı.
 *
 * ### ⚠ HTML ÜRETİLMİYOR, düğüm üretiliyor
 *
 * İşaretler `text` içine ofsetle bakıyor ve burada React düğümlerine çevriliyor.
 * `dangerouslySetInnerHTML` ile HTML kursaydık ADR-0026'nın temizleme borcunu tam da
 * kaçındığı yerden geri getirirdik: sözleşme metni kullanıcı girdisi.
 */
export function ContractBlockText({ blok }: { blok: ContractBlock }) {
  const icerik = parcalara(blok);

  if (blok.kind === "heading") {
    /**
     * ⚠ Madde İÇİ ara başlık `h3`'ten başlıyor: madde başlığı zaten `h2`. `h2` yazsaydık
     * ekran okuyucunun başlık ağacında madde ile onun alt başlığı aynı düzeyde durur ve
     * belge yapısı yanlış okunurdu.
     */
    const Etiket = blok.level === 1 ? "h3" : blok.level === 3 ? "h5" : "h4";
    return (
      <Etiket className={`text-sm font-semibold text-fg-heading ${hizalama(blok)}`}>
        {icerik}
      </Etiket>
    );
  }

  if (blok.kind === "bullet" || blok.kind === "ordered") {
    /**
     * ⚠ İşaret metne YAZILMIYOR, CSS ile çiziliyor. Metne "• " eklesek kopyalayan kullanıcı
     * işareti de kopyalar ve karşılaştırma iki farklı metin görürdü.
     */
    const isaret = blok.kind === "bullet" ? "before:content-['•']" : "before:content-['–']";
    return (
      <p className={`pl-4 text-sm text-fg before:mr-1 ${isaret} ${hizalama(blok)}`}>{icerik}</p>
    );
  }

  return <p className={`text-sm text-fg ${hizalama(blok)}`}>{icerik}</p>;
}

function hizalama(blok: ContractBlock): string {
  if (blok.align === "center") return "text-center";
  if (blok.align === "right") return "text-right";
  if (blok.align === "justify") return "text-justify";
  return "";
}

/**
 * Metni işaret aralıklarına göre parçalara böler.
 *
 * ⚠ Sınırlar İŞARET UÇLARINDAN toplanıyor, işaret başına parça üretilmiyor — çakışan
 * kalın+italik aralıklarında metin iki kez yazılmasın (`belgeEditorModeli`'nde aynı
 * gerekçe ve orada mutasyonla doğrulandı).
 */
function parcalara(blok: ContractBlock) {
  const isaretler = blok.marks ?? [];
  if (isaretler.length === 0) return blok.text;

  const sinirlar = new Set<number>([0, blok.text.length]);
  for (const isaret of isaretler) {
    sinirlar.add(Math.max(0, isaret.start));
    sinirlar.add(Math.min(blok.text.length, isaret.end));
  }
  const sirali = [...sinirlar].sort((a, b) => a - b);

  const parcalar: React.ReactNode[] = [];
  for (let i = 0; i < sirali.length - 1; i += 1) {
    const bas = sirali[i]!;
    const son = sirali[i + 1]!;
    const metin = blok.text.slice(bas, son);
    if (!metin) continue;

    const turler = new Set(
      isaretler.filter((m) => m.start <= bas && m.end >= son).map((m) => m.type),
    );
    let dugum: React.ReactNode = metin;
    if (turler.has("underline")) dugum = <u key={`u${bas}`}>{dugum}</u>;
    if (turler.has("italic")) dugum = <em key={`i${bas}`}>{dugum}</em>;
    if (turler.has("bold")) dugum = <strong key={`b${bas}`}>{dugum}</strong>;
    parcalar.push(<span key={bas}>{dugum}</span>);
  }
  return parcalar;
}
